import { fetchPanelStatus, phaseFromPanelStatus } from '@/api/panel-status';
import { socket } from '@/api/socket';
import { ServerStatusOverlay } from '@/components/server-status-overlay';
import { $connectionPhase, $setConnectionPhase } from '@/entities/connection-entity';
import { $profile } from '@/entities/profile-entity';
import { isSenderApp } from '@/lib/app-mode';
import { isDevMode } from '@/lib/is-dev-mode';
import { shouldPollPanelStatus } from '@/lib/panel-status-poll';
import { useUnit } from 'effector-react';
import { useEffect, type PropsWithChildren } from 'react';

const PANEL_POLL_INTERVAL_MS = 2000;
const SENDER_POLL_INTERVAL_MS = 30_000;

export default function ServerStatusProvider({ children }: PropsWithChildren) {
	const [connectionPhase, setConnectionPhase, profile] = useUnit([$connectionPhase, $setConnectionPhase, $profile]);
	const pollIntervalMs = isSenderApp ? SENDER_POLL_INTERVAL_MS : PANEL_POLL_INTERVAL_MS;

	useEffect(() => {
		if (!shouldPollPanelStatus(isSenderApp, connectionPhase, isDevMode)) return;

		let cancelled = false;

		async function pollStatus() {
			try {
				const status = await fetchPanelStatus();
				if (cancelled) return;

				const nextPhase = phaseFromPanelStatus(status);
				setConnectionPhase(nextPhase);

				if (!isSenderApp && nextPhase === 'online' && profile && !socket.connected) {
					socket.connect();
				}
			} catch {
				if (!cancelled && connectionPhase !== 'updating') {
					setConnectionPhase('unavailable');
				}
			}
		}

		void pollStatus();
		const timer = setInterval(() => {
			void pollStatus();
		}, pollIntervalMs);

		return () => {
			cancelled = true;
			clearInterval(timer);
		};
	}, [connectionPhase, pollIntervalMs, profile, setConnectionPhase]);

	if (isDevMode || connectionPhase === 'online') {
		return <>{children}</>;
	}

	return (
		<>
			{children}
			<ServerStatusOverlay phase={connectionPhase} />
		</>
	);
}
